/**
 * @file   src\pages\Notifications.js
 * @brief  This file is responsible for notifications settings.
 * @date   June, 2023
 * @author ZCO Engineer
 * @copyright (c) 2023, ZCO
 */
import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import {
  getNotifications,
  saveNotifications,
} from "../services/userprofileService";
import { trackEvent } from "../services/analytics";
import { getLocalStorage, getUserId } from "../helpers/common";
import {
  buildSettingsState,
  applyToggleResult,
  isUnsubscribedFromAll,
  buildAllTogglePayload,
  applyAllToggleResult,
  toggleValueForUnsubscribeAll,
  UNSUBSCRIBE_ELIGIBLE_KEYS,
} from "../helpers/notificationSettings";
import { getAlertBudgetStatus } from "../services/alertBudgetService";
import { USER_INFO_KEY, USER_TYPE_AGENT } from "../constants/common";

const NOTIFICATION_SETTINGS_KEY = "notificationSettings";
const ALERT_BUDGET_KEY = "alertBudgetStatus";

// Grouped for display only - each key saves as its own independent field,
// since the API does a true partial update.
const NOTIFICATION_GROUPS = [
  {
    title: "Matches & alerts",
    toggles: [
      {
        key: "Matches",
        label: "New matches",
        description: "Get notified about new matches to your property",
      },
      {
        key: "MatchRecap",
        label: "Match recap",
        description: "Get a periodic recap of your recent matches",
      },
      {
        key: "WeeklyDigest",
        agentOnly: true,
        budgetOnly: true,
        label: "Recaps and reminders",
        description:
          "A monthly recap of Buyer Want-Listings in your network, and a reminder when you might have a new buyer",
      },
      {
        key: "MlsMatchDigest",
        label: "Daily buyer-demand digest",
        description:
          "Get a daily digest of buyers whose Want-Listings match your MLS listings",
      },
      {
        key: "StandoutMatch",
        label: "Standout match alerts",
        description: "Get notified when a strong property match is found",
      },
      {
        key: "UpgradeNudge",
        label: "Upgrade reminders",
        description: "Get occasional reminders about upgrading your plan",
      },
    ],
  },
  {
    title: "Listing activity",
    toggles: [
      {
        key: "ListingSyndicated",
        label: "Listing syndication",
        description: "Get notified when your Want-Listing is syndicated",
      },
      {
        key: "ListingActivity",
        label: "Listing activity & tips",
        description:
          "Get notified about your listing's status and tips to close more deals",
      },
    ],
  },
  {
    title: "Messages & introductions",
    toggles: [
      {
        key: "Messages",
        label: "Messages",
        description: "Get notified when another member messages me directly",
      },
      {
        key: "Introductions",
        label: "Introductions",
        description: "Get notified when another member sends me an introduction",
      },
    ],
  },
  {
    title: "Getting started",
    toggles: [
      {
        key: "Onboarding",
        label: "Setup tips",
        description:
          "Occasional tips while you set up your first Buyer Want-Listing and homes",
      },
    ],
  },
  {
    title: "Account & billing",
    toggles: [
      {
        key: "AccountActivity",
        label: "Account activity",
        description: "Get notified about important account activity",
      },
    ],
  },
];

const Notifications = () => {
  const queryClient = useQueryClient();
  const userId = getUserId();
  const isAgent =
    Number(getLocalStorage(USER_INFO_KEY)?.userInfo?.userType) ===
    USER_TYPE_AGENT;
  const queryKey = [NOTIFICATION_SETTINGS_KEY, userId];

  const alertBudgetQuery = useQuery({
    queryKey: [ALERT_BUDGET_KEY, userId],
    queryFn: getAlertBudgetStatus,
    enabled: !!userId && isAgent,
    staleTime: 5 * 60 * 1000,
  });
  const isAlertBudgetActive = isAgent && alertBudgetQuery.data === true;

  const settingsQuery = useQuery({
    queryKey,
    queryFn: async () => {
      const response = await getNotifications();
      if (response?.errorCode !== 0) {
        throw new Error(
          response?.message || "Failed to load notification settings"
        );
      }
      return buildSettingsState(response?.result?.[0]);
    },
    enabled: !!userId,
    // Email unsubscribe links change these outside the app, so re-read on
    // every visit.
    staleTime: 0,
  });
  // Until the fetch resolves, show the API's own missing-row defaults rather
  // than an all-off state. A failed refetch keeps the last-known-good data.
  const settings = settingsQuery.data ?? buildSettingsState();
  const isLoadingSettings = settingsQuery.isPending || settingsQuery.isFetching;
  const hasLoadError = settingsQuery.isError;

  const saveMutation = useMutation({
    mutationFn: async (payload) => {
      const response = await saveNotifications(payload);
      if (response?.errorCode !== 0) {
        throw new Error("Failed to save notification settings");
      }
      return response;
    },
  });
  // Blocks a second save from starting, so its revert-on-failure snapshot
  // can't be taken from state a still-pending save is about to overwrite.
  const isSaving = saveMutation.isPending;

  // Optimistic writes and reverts go straight to the cache, which keeps it
  // accurate for the next visit.
  const setSettings = (update) =>
    queryClient.setQueryData(queryKey, (prev) =>
      update(prev ?? buildSettingsState())
    );

  const persist = (payload) =>
    saveMutation.mutateAsync(payload).then(
      () => true,
      () => false
    );

  // Sends only the changed key, then reverts and shows an error if the save
  // failed - a failed unsubscribe must never look like it worked.
  const handleToggle = async (key, value) => {
    if (isSaving) return;
    const previousValue = !!settings[key];
    setSettings((prev) => ({ ...prev, [key]: value }));

    const saveSucceeded = await persist({ [key]: value });

    setSettings((prev) =>
      applyToggleResult(prev, key, value, previousValue, saveSucceeded)
    );

    if (saveSucceeded) {
      toast.dismiss();
      toast.success("Notification settings saved successfully.");
    } else {
      toast.dismiss();
      toast.error(
        "Something went wrong saving your preference. Please try again."
      );
      // Nothing else surfaces these toasts, so this is the only signal that
      // saves are broken for everyone (e.g. a deploy-ordering mismatch).
      trackEvent({
        name: "notification_settings_save_failed",
        attributes: { key, scope: "single" },
      });
    }
  };

  const isToggleVisible = (toggle) =>
    (isAgent || !toggle.agentOnly) && (isAlertBudgetActive || !toggle.budgetOnly);
  const visibleKeys = NOTIFICATION_GROUPS.flatMap((group) =>
    group.toggles.filter(isToggleVisible).map((toggle) => toggle.key)
  ).filter((key) => UNSUBSCRIBE_ELIGIBLE_KEYS.includes(key));
  const allUnsubscribed = isUnsubscribedFromAll(settings, visibleKeys);

  // Takes the checkbox's target *checked* state, not the per-category toggle
  // value - those are inverses, and toggleValueForUnsubscribeAll is the single
  // place that conversion happens. Don't re-derive it inline.
  const handleToggleAll = async (shouldUnsubscribeAll) => {
    if (isSaving) return;
    const toggleValue = toggleValueForUnsubscribeAll(shouldUnsubscribeAll);
    const previousValues = UNSUBSCRIBE_ELIGIBLE_KEYS.reduce(
      (acc, key) => ({ ...acc, [key]: !!settings[key] }),
      {}
    );
    setSettings((prev) => ({
      ...prev,
      ...buildAllTogglePayload(toggleValue),
    }));

    const saveSucceeded = await persist(buildAllTogglePayload(toggleValue));

    setSettings((prev) =>
      applyAllToggleResult(prev, previousValues, toggleValue, saveSucceeded)
    );

    if (saveSucceeded) {
      toast.dismiss();
      toast.success(
        shouldUnsubscribeAll
          ? "Unsubscribed from all email notifications."
          : "Notification settings saved successfully."
      );
    } else {
      toast.dismiss();
      toast.error(
        "Something went wrong saving your preference. Please try again."
      );
      trackEvent({
        name: "notification_settings_save_failed",
        attributes: { scope: "all" },
      });
    }
  };

  return (
    <div className="notification-wrap">
      <div className="title-sec">
        <h4>Email notifications</h4>
      </div>
      {hasLoadError && (
        <p
          className="error-msg"
          role="alert"
          style={{ height: "auto", textTransform: "none" }}
        >
          Couldn't load your current notification settings. Toggles below
          may not reflect what's saved - please refresh before making
          changes.
        </p>
      )}
      {NOTIFICATION_GROUPS.map((group) => (
        <div className="notification-group" key={group.title}>
          <h5 className="notification-group-title">{group.title}</h5>
          {group.toggles
            .filter(isToggleVisible)
            .map((toggle) => {
              const descriptionId = `${toggle.key}-description`;
              return (
                <div className="field-wrap" key={toggle.key}>
                  <label className="form-check-label checkbox-label">
                    <p>{toggle.label}</p>
                    <span id={descriptionId}>{toggle.description}</span>
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={!!settings[toggle.key]}
                      disabled={isLoadingSettings || hasLoadError || isSaving}
                      aria-describedby={descriptionId}
                      onChange={() =>
                        handleToggle(toggle.key, !settings[toggle.key])
                      }
                      id={`${toggle.key}check`}
                    />
                  </label>
                </div>
              );
            })}
        </div>
      ))}
      <div className="notification-group notification-master-toggle">
        <div className="field-wrap">
          <label className="form-check-label checkbox-label">
            <p>Unsubscribe from all email notifications</p>
            <span id="unsubscribe-all-description">
              Turn off every alert type below in one step. Account activity
              (billing) emails are unaffected.
            </span>
            <input
              className="form-check-input"
              type="checkbox"
              checked={allUnsubscribed}
              disabled={isLoadingSettings || hasLoadError || isSaving}
              aria-describedby="unsubscribe-all-description"
              onChange={() => handleToggleAll(!allUnsubscribed)}
              id="unsubscribeAllCheck"
            />
          </label>
        </div>
      </div>
    </div>
  );
};

export default Notifications;
